import { KeyboardTimePicker } from "@material-ui/pickers";
import React, { useCallback, useMemo } from "react";
import CcFieldLayout, {
  resolveFieldMessage,
  useCcInputStyles,
} from "./CcFieldLayout";

function parseTimeValue(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  if (!text) return null;
  const match = text.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const date = new Date();
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatTimeValue(date) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

const CcTimePicker = React.memo(
  ({
    label,
    required = false,
    value,
    onChange,
    format = "hh:mm aa",
    clearable = true,
    error,
    helperText,
    hint,
    id,
    onBlur,
    KeyboardButtonProps,
    InputLabelProps: _inputLabelProps,
    ...props
  }) => {
    const inputClasses = useCcInputStyles();
    const { hasError, message } = resolveFieldMessage({
      error,
      helperText,
      hint,
    });
    const parsedValue = useMemo(() => parseTimeValue(value), [value]);
    const handleChange = useCallback(
      (newValue) => {
        onChange?.(formatTimeValue(newValue));
      },
      [onChange],
    );

    const picker = (
      <KeyboardTimePicker
        autoOk
        ampm
        variant="inline"
        inputVariant="outlined"
        format={format}
        clearable={clearable}
        value={parsedValue}
        onChange={handleChange}
        onBlur={onBlur}
        id={id}
        error={hasError}
        helperText={undefined}
        label={undefined}
        className={inputClasses.root}
        InputLabelProps={{ shrink: false, style: { display: "none" } }}
        FormHelperTextProps={{ style: { display: "none" } }}
        placeholder="hh:mm am"
        KeyboardButtonProps={{
          "aria-label": "change time",
          ...KeyboardButtonProps,
        }}
        {...props}
      />
    );

    if (!label && !message) {
      return picker;
    }

    return (
      <CcFieldLayout
        label={label}
        required={required}
        error={hasError ? message : undefined}
        hint={!hasError ? message : undefined}
        htmlFor={id}
      >
        {picker}
      </CcFieldLayout>
    );
  },
);

CcTimePicker.displayName = "CcTimePicker";

export default CcTimePicker;
